import { BarColor } from '@factorialco/deprecated-design-system'
import { EmployeeTimePeriodDetailsFragment } from 'generated/resources/graphql'

import { nameFromHexCode } from 'modules/shift_management/lib/colors'

import StackedBarsTooltip from './StackedBarsTooltip'

interface Bar {
  hoursType: number
  hours: number
  label: React.ReactNode
  color: BarColor
}

enum EmployeeHoursPriority {
  StandardPlannedHours = 0,
  PaidLeaveHours = 1,
  OverPlannedHoursWithoutAdditional = 2,
  RemainingHoursWithoutAdditional = 3,
}

export const hoursBars = (employeeData: EmployeeTimePeriodDetailsFragment, rangeType: string) => {
  const {
    standardPlannedHours,
    paidLeavesHours,
    overplannedHoursWithoutAdditional,
    remainingHoursWithoutAdditional,
    additionalHoursDetails,
  } = employeeData

  const bars = [
    standardPlannedHours,
    paidLeavesHours,
    overplannedHoursWithoutAdditional,
    remainingHoursWithoutAdditional,
  ].map((rawHours, hoursType): Bar => {
    let color: BarColor
    let label: React.ReactNode
    let hours = Number(rawHours)

    switch (hoursType) {
      case EmployeeHoursPriority.StandardPlannedHours:
        color = [nameFromHexCode('viridian1000'), nameFromHexCode('viridian200')]
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('viridian1000')}
            hours={standardPlannedHours}
            additionalHoursDetails={additionalHoursDetails}
          />
        )
        break
      case EmployeeHoursPriority.PaidLeaveHours:
        color = [nameFromHexCode('champagne1000'), nameFromHexCode('champagne200')]
        label = (
          <StackedBarsTooltip color={nameFromHexCode('champagne1000')} hours={paidLeavesHours} />
        )
        break
      case EmployeeHoursPriority.OverPlannedHoursWithoutAdditional:
        color = [nameFromHexCode('tangerine1000'), nameFromHexCode('tangerine200')]
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('tangerine1000')}
            hours={overplannedHoursWithoutAdditional}
          />
        )
        break
      default:
        color = [nameFromHexCode('grey400'), nameFromHexCode('grey200')]
        // We need to swap it because bars need positive numbers. Negative numbers
        // will appear as empty. We then re-add the negative sign to match the original value
        hours = 0 - hours
        const remainingToAssign = `-${String(hours)}`
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('grey400')}
            hours={remainingToAssign}
            rangeType={rangeType}
          />
        )
    }
    return {
      hoursType,
      hours,
      label,
      color,
    }
  })

  return {
    bars,
    remainingHours: remainingHoursWithoutAdditional,
  }
}

enum EmployeeConvertedHoursPriority {
  StandardPlannedHours = 0,
  AdditionalPlannedHours = 1,
  PaidLeaveHours = 2,
  OverPlannedHours = 3,
  RemainingHours = 4,
}

export const convertHoursBars = (
  employeeData: EmployeeTimePeriodDetailsFragment,
  rangeType: string
) => {
  const {
    standardPlannedHours,
    additionalPlannedHours,
    paidLeavesHours,
    overplannedHours,
    remainingHours,
    additionalHoursDetails,
  } = employeeData

  const bars = [
    standardPlannedHours,
    additionalPlannedHours,
    paidLeavesHours,
    overplannedHours,
    remainingHours,
  ].map((rawHours, hoursType): Bar => {
    let color: BarColor
    let label: React.ReactNode
    let hours = Number(rawHours)

    switch (hoursType) {
      case EmployeeConvertedHoursPriority.StandardPlannedHours:
        color = [nameFromHexCode('viridian1000'), nameFromHexCode('viridian200')]
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('viridian1000')}
            hours={standardPlannedHours}
            additionalHoursDetails={additionalHoursDetails}
          />
        )
        break
      case EmployeeConvertedHoursPriority.AdditionalPlannedHours:
        color = [nameFromHexCode('malibu1000'), nameFromHexCode('malibu200')]
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('malibu1000')}
            hours={additionalPlannedHours}
            additionalHoursDetails={additionalHoursDetails}
          />
        )
        break
      case EmployeeConvertedHoursPriority.PaidLeaveHours:
        color = [nameFromHexCode('champagne1000'), nameFromHexCode('champagne200')]
        label = (
          <StackedBarsTooltip color={nameFromHexCode('champagne1000')} hours={paidLeavesHours} />
        )
        break
      case EmployeeConvertedHoursPriority.OverPlannedHours:
        color = [nameFromHexCode('tangerine1000'), nameFromHexCode('tangerine200')]
        label = (
          <StackedBarsTooltip color={nameFromHexCode('tangerine1000')} hours={overplannedHours} />
        )
        break
      default:
        color = [nameFromHexCode('grey400'), nameFromHexCode('grey200')]
        // We need to swap it because bars need positive numbers. Negative numbers
        // will appear as empty. We then re-add the negative sign to match the original value
        hours = 0 - hours
        const remainingToAssign = `-${String(hours)}`
        label = (
          <StackedBarsTooltip
            color={nameFromHexCode('grey400')}
            hours={remainingToAssign}
            rangeType={rangeType}
          />
        )
    }
    return {
      hoursType,
      hours,
      label,
      color,
    }
  })

  return {
    bars,
    remainingHours,
  }
}
